import React, { FunctionComponent, useState, useCallback } from 'react'
import { Button, WithPopover } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { IconCallSplitSolid24 } from '@lightspeed/unified-react-components'
import { vn } from 'vend-number'
import { LineItem } from '../../../../models/line-item'
import { ERRORS } from '../../../../constants'
import { useSaleManager } from '../../../../ng-compat/hooks'
import { LineItemSplitQuantityPopover } from './LineItemSplitQuantityPopover'

interface Props {
  lineItem: LineItem
  disabled?: boolean
}

export const LineItemSplitQuantityButton: FunctionComponent<Props> = ({
  lineItem,
  disabled = false,
}) => {
  const saleManager = useSaleManager()

  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)
  const [splitQuantity, setSplitQuantity] = useState<number>(0)
  const [inputError, setInputError] = useState<string | null>(null)

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false)
    setSplitQuantity(0)
    setInputError(null)
  }, [])

  const handleSplitQuantity = useCallback(() => {
    closePopover()

    saleManager.splitLineItem(lineItem, splitQuantity)

    // omit closePopover (empty dependency array) and saleManager (from context) as they are stable references that don't need to trigger re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItem, splitQuantity])

  const validateSplitQuantity = useCallback(
    (value: number) => {
      if (vn(value).lessThan(0)) {
        return ERRORS.INVALID_MIN
      }

      // for positive line item quantity: split quantity can't exceed line item quantity
      const isPositiveQuantity = vn(lineItem.quantity).greaterThan(0)
      const maxQuantity = isPositiveQuantity
        ? lineItem.quantity
        : vn(lineItem.quantity).abs()

      // for negative line item quantity (returns): split quantity can't exceed absolute value of line item quantity
      if (vn(value).greaterThan(maxQuantity)) {
        return ERRORS.INVALID_MAX
      }

      return null
    },
    [lineItem.quantity]
  )

  const handleQuantityChange = useCallback(
    (value: string) => {
      const numValue = Number(value)
      const error = validateSplitQuantity(numValue)

      if (error) {
        setInputError(error)
        setSplitQuantity(0)
      } else {
        setInputError(null)
        setSplitQuantity(numValue)
      }
    },
    [validateSplitQuantity]
  )

  const handleQuantityError = useCallback((error: string | null) => {
    setInputError(error)
  }, [])

  const hasError = inputError !== null

  return (
    <WithPopover insideModal={true} direction="left" alignment="center">
      <div className="li-cell-actions">
        <Button
          modifier="text"
          variant="supplementary"
          onClick={() => setIsPopoverOpen(true)}
          disabled={disabled}
          className="vd-flex vd-flex--align-center vd-flex--justify-left li-cell-split-quantity-button"
          data-track="line-item-split-quantity-button"
        >
          <IconCallSplitSolid24 data-testid="split-quantity-icon" />
          <T _str="Split quantity" _context="Line item split quantity button" />
        </Button>
      </div>
      <LineItemSplitQuantityPopover
        isOpen={isPopoverOpen}
        onClose={closePopover}
        onConfirm={handleSplitQuantity}
        lineItem={lineItem}
        splitQuantity={splitQuantity}
        inputError={inputError}
        onQuantityChange={handleQuantityChange}
        onQuantityError={handleQuantityError}
        hasError={hasError}
      />
    </WithPopover>
  )
}
